import { Customer } from "../types/customer.data";
import { useAppSelector } from '../../../redux';
import { CUSTOMER_STORE_NAME } from "../../../contants/stores";
import { formatDate } from "../../../utils/helpers";
import Table from "../../../components/Table/Table";
import { useEffect, useState, useCallback } from "react";
import { search, updateQuery, searchAllCustomers, getAllCustomers, filterAllCustomersAction } from "../actions/customer.actions";
import * as XLSX from 'xlsx';
import saveAs from 'file-saver';
import CustomerWalletDialog from './CustomerWalletDialog';
import { fetchAllCustomers, getCustomersWithWalletTxBetween } from '../services/customer.services';

export const CustomerList = (props: any) => {
  const customers = useAppSelector((state: any) => state[CUSTOMER_STORE_NAME].customers);
  const query = useAppSelector((state: any) => state[CUSTOMER_STORE_NAME].searchQuery);
  const headers = ["Name", "Phone", "Email", "Wallet Balance", "Status", "Created At", "Action"];
  const [filteredCustomers, setFilteredCustomers] = useState(customers);
  const [isSearching, setIsSearching] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [walletMin, setWalletMin] = useState('');
  const [walletMax, setWalletMax] = useState('');
  const [walletTxFrom, setWalletTxFrom] = useState('');
  const [walletTxTo, setWalletTxTo] = useState('');

  // Use pagination props from parent
  const currentPage = props.currentPage || 1;
  const setCurrentPage = props.setCurrentPage;
  const rowsPerPage = props.rowsPerPage || 10;
  const setRowsPerPage = props.setRowsPerPage;

  const [loading, setLoading] = useState(true);

  const [showWalletDialog, setShowWalletDialog] = useState(false);
  const [selectedCustomerForWallet, setSelectedCustomerForWallet] = useState<any>(null);

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchQuery: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (searchQuery.trim().length > 0) {
            setIsSearching(true);
            searchAllCustomers(searchQuery).finally(() => setIsSearching(false));
          } else {
            // If search is empty, reset to first page and trigger paginated fetch
            if (setCurrentPage) {
              setCurrentPage(1);
              // Force a fresh fetch by calling getAllCustomers directly
              setTimeout(() => {
                getAllCustomers(rowsPerPage, 0);
              }, 100);
            }
          }
        }, 500); // 500ms delay
      };
    })(),
    [setCurrentPage, rowsPerPage]
  );

  // Debounced filter function
  const debouncedFilter = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (filters: any) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setIsFiltering(true);
          filterAllCustomersAction(filters).finally(() => setIsFiltering(false));
        }, 300); // 300ms delay for filters
      };
    })(),
    []
  );

  useEffect(() => {
    setLoading(true);
    updateSearchList();
  }, [customers]);

  useEffect(() => {
    // Use debounced search for server-side search
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  useEffect(() => {
    if (!isSearching && !isFiltering) {
      setLoading(false);
    }
  }, [isSearching, isFiltering, filteredCustomers, customers]);

  // Apply filters to current data (for display purposes)
  const applyFilters = (list: Customer[]) => {
    return list.filter((customer: Customer) => {
      // Status filter
      if (statusFilter !== 'ALL') {
        if (statusFilter === 'ACTIVE' && customer.isBlocked) return false;
        if (statusFilter === 'BLOCKED' && !customer.isBlocked) return false;
      }
      // Wallet balance filter
      if (walletMin && (!customer.walletBalance || customer.walletBalance < parseFloat(walletMin))) return false;
      if (walletMax && (!customer.walletBalance || customer.walletBalance > parseFloat(walletMax))) return false;
      // Created at filter
      let createdAtDate: Date | null = null;
      if (customer.createdAt) {
        createdAtDate = customer.createdAt.toDate ? customer.createdAt.toDate() : new Date(customer.createdAt);
      }
      if (createdAtDate && (!createdAtDate || createdAtDate < new Date(walletTxFrom))) return false;
      if (createdAtDate && (!createdAtDate || createdAtDate > new Date(walletTxTo))) return false;
      return true;
    });
  };

  const updateSearchList = () => {
    // For now, just use the customers directly since server-side filtering handles the heavy lifting
    setFilteredCustomers(customers);
  };

  // When filters change, trigger server-side filtering (only if no wallet tx date filter is active)
  useEffect(() => {
    // Only apply other filters if wallet transaction date filter is not active
    if (!walletTxFrom && !walletTxTo) {
      const filters = {
        status: statusFilter,
        walletMin: walletMin ? parseFloat(walletMin) : undefined,
        walletMax: walletMax ? parseFloat(walletMax) : undefined,
        walletTxFrom: walletTxFrom || undefined,
        walletTxTo: walletTxTo || undefined
      };
      
      debouncedFilter(filters);
    }
  }, [statusFilter, walletMin, walletMax, walletTxFrom, walletTxTo, debouncedFilter]);

  // When wallet transaction date filter changes, fetch customers with wallet tx in range
  useEffect(() => {
    const fetchByWalletTx = async () => {
      // If either walletTxFrom or walletTxTo is set, use wallet tx filter
      if (walletTxFrom || walletTxTo) {
        setLoading(true);
        // Use min/max dates if one is missing
        const from = walletTxFrom || '1970-01-01';
        const to = walletTxTo || '2100-12-31';
        const customersWithTx = await getCustomersWithWalletTxBetween(from, to);
        // Apply other filters and search client-side
        let filtered = customersWithTx;
        // Status filter
        if (statusFilter !== 'ALL') {
          filtered = filtered.filter((customer: Customer) => {
            if (statusFilter === 'ACTIVE' && customer.isBlocked) return false;
            if (statusFilter === 'BLOCKED' && !customer.isBlocked) return false;
            return true;
          });
        }
        // Wallet min/max
        if (walletMin) filtered = filtered.filter((c: Customer) => c.walletBalance && c.walletBalance >= parseFloat(walletMin));
        if (walletMax) filtered = filtered.filter((c: Customer) => c.walletBalance && c.walletBalance <= parseFloat(walletMax));
        // Search
        if (query.trim().length > 0) {
          const q = query.trim().toLowerCase();
          filtered = filtered.filter((c: Customer) =>
            (c.name && c.name.toLowerCase().includes(q)) ||
            (c.email && c.email.toLowerCase().includes(q)) ||
            (c.phone && c.phone.toLowerCase().includes(q))
          );
        }
        setFilteredCustomers(filtered);
        setLoading(false);
      } else {
        // If no wallet tx date filter, show all customers (with other filters/search applied)
        setFilteredCustomers(customers);
      }
    };
    fetchByWalletTx();
    // eslint-disable-next-line
  }, [walletTxFrom, walletTxTo, customers, statusFilter, walletMin, walletMax, query]);

  // Reset to first page when filters/search change
  useEffect(() => {
    if (setCurrentPage && query.trim().length === 0) setCurrentPage(1);
  }, [filteredCustomers.length, query, setCurrentPage]);

  // Since backend handles pagination, we use all customers from props
  // No need for local pagination slicing
  const displayCustomers = filteredCustomers;

  const exportToExcel = () => {
    // Prepare data for export
    const data = filteredCustomers.map((customer: Customer) => ({
      Name: customer.name || '',
      Phone: customer.phone || '',
      Email: customer.email || '',
      'Wallet Balance': customer.walletBalance ? customer.walletBalance.toFixed(2) : '0.00',
      Status: customer.isBlocked ? 'Blocked' : 'Active',
      'Created At': customer.createdAt ? (customer.createdAt.toDate ? formatDate(customer.createdAt.toDate()) : formatDate(customer.createdAt)) : '',
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'customers.xlsx');
  };

  // Export all customers (not filtered)
  const exportAllCustomers = async () => {
    // Fetch all customers from Firestore
    const allCustomers = await fetchAllCustomers(10000, 0); // Adjust limit as needed
    const data = allCustomers.map((customer: Customer) => ({
      Name: customer.name || '',
      Phone: customer.phone || '',
      Email: customer.email || '',
      'Wallet Balance': customer.walletBalance ? customer.walletBalance.toFixed(2) : '0.00',
      Status: customer.isBlocked ? 'Blocked' : 'Active',
      'Created At': customer.createdAt ? (customer.createdAt.toDate ? formatDate(customer.createdAt.toDate()) : formatDate(customer.createdAt)) : '',
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Customers");
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'all_customers.xlsx');
  };

  return (
    <>
      {/* Unified Search, Filter, and Export Bar */}
      <div className="w-full flex gap-2 mb-4 items-center">
        {/* Search Input - takes all available width */}
        <div className="flex-1 min-w-0 relative">
          <input
            type="text"
            className="border border-gray-300 rounded px-3 py-2 text-sm w-full h-10 pr-8"
            placeholder="Search customers by name, phone, or email..."
            value={query}
            onChange={e => updateQuery(e.target.value)}
          />
          {isSearching && (
            <div className="absolute right-2 top-2">
              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
            </div>
          )}
        </div>
        {/* Filters and Export Button - right aligned */}
        <div className="flex flex-wrap gap-2 items-center">
          {/* Status Filter */}
          <select
            className="border border-gray-300 rounded px-2 py-1 text-sm h-10"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="BLOCKED">Blocked</option>
          </select>
          {/* Wallet Min/Max */}
          <input
            type="number"
            className="border border-gray-300 rounded px-2 py-1 text-sm w-24 h-10"
            placeholder="Min Wallet"
            value={walletMin}
            onChange={e => setWalletMin(e.target.value)}
          />
          <input
            type="number"
            className="border border-gray-300 rounded px-2 py-1 text-sm w-24 h-10"
            placeholder="Max Wallet"
            value={walletMax}
            onChange={e => setWalletMax(e.target.value)}
          />
          {/* Wallet Transaction Date Range Filter */}
          <input
            type="date"
            className="border border-gray-300 rounded px-2 py-1 text-sm h-10"
            value={walletTxFrom}
            onChange={e => setWalletTxFrom(e.target.value)}
            placeholder="Wallet Tx From"
          />
          <input
            type="date"
            className="border border-gray-300 rounded px-2 py-1 text-sm h-10"
            value={walletTxTo}
            onChange={e => setWalletTxTo(e.target.value)}
            placeholder="Wallet Tx To"
          />
          {/* Filter Status Indicator */}
          {isFiltering && (
            <div className="flex items-center gap-1 text-xs text-blue-600">
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              Filtering...
            </div>
          )}
          {/* Export Button */}
          <button
            onClick={exportToExcel}
            className="h-10 pl-5 pr-2 flex items-center bg-blue-600 text-white font-medium text-xs leading-normal uppercase rounded shadow-md hover:bg-blue-700 hover:shadow-lg focus:bg-blue-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-blue-800 active:shadow-lg transition duration-150 ease-in-out"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h8a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
            </svg>
            Export to Excel
          </button>
          {/* Export All Button */}
          <button
            onClick={exportAllCustomers}
            className="h-10 pl-5 pr-2 flex items-center bg-green-600 text-white font-medium text-xs leading-normal uppercase rounded shadow-md hover:bg-green-700 hover:shadow-lg focus:bg-green-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-green-800 active:shadow-lg transition duration-150 ease-in-out"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h8a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
            </svg>
            Export All
          </button>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center items-center min-h-[300px]">
          <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
          </svg>
        </div>
      ) : query.length > 0 && displayCustomers.length === 0 ? (
        <div className="w-full order-1 flex-1 bg-white rounded-lg m-4 text-xs text-center py-8 text-gray-500">
          No results found
        </div>
      ) : displayCustomers.length === 0 ? (
        <div className="w-full order-1 flex-1 bg-white rounded-lg m-4 text-xs text-center py-8 text-gray-500">
          No customers found
        </div>
      ) : (
        <>
          <Table headers={headers}>
            {displayCustomers && displayCustomers.map((customer: Customer) => (
              <tr className="bg-white border-b hover:bg-gray-50 text-xs" key={customer?.id}>
                <th scope="row" className="flex items-center py-4 px-6 text-gray-900 whitespace-nowra text-xs">
                  <div className="">
                    <div className="flex items-center">
                      <div className={`h-2.5 w-2.5 rounded-full bg-${customer?.isBlocked ? "red-500" : "green-400"} mr-2`}></div>
                      <div className="text-sm font-semibold text-gray-700">{customer?.name || 'N/A'}</div>
                    </div>
                  </div>
                </th>
                <td className="py-4 px-6">
                  {customer?.phone || 'N/A'}
                </td>
                <td className="py-4 px-6">
                  {customer?.email || 'N/A'}
                </td>
                <td className="py-4 px-6">
                  <p className="text-xs font-semibold text-gray-700">
                    {customer?.walletBalance ? `${customer.walletBalance.toFixed(2)} KD` : '0.00 KD'}
                  </p>
                </td>
                <td className="py-4 px-6">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    customer?.isBlocked 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {customer?.isBlocked ? 'Blocked' : 'Active'}
                  </span>
                </td>
                <td className="py-4 px-6">
                  {customer?.createdAt ? 
                    (customer.createdAt.toDate ? 
                      formatDate(customer.createdAt.toDate()) : 
                      formatDate(customer.createdAt)
                    ) : 'N/A'}
                </td>
                <td className="flex justify-center gap-2 py-4 px-6">
                  <button
                    onClick={() => props.onPressItem(customer, 'VIEW_ORDERS')}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="View Orders">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => { setSelectedCustomerForWallet(customer); setShowWalletDialog(true); }}
                    className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                    title="View Wallet Transactions">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 10c-4.41 0-8-1.79-8-4V6c0-2.21 3.59-4 8-4s8 1.79 8 4v8c0 2.21-3.59 4-8 4z" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </Table>
          {/* Pagination Controls - only show when not searching or filtering */}
          {query.trim().length === 0 && !isFiltering && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-4 gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Rows per page:</span>
                <select
                  className="border border-gray-300 rounded px-2 py-1 text-xs"
                  value={rowsPerPage}
                  onChange={e => { 
                    if (setRowsPerPage) setRowsPerPage(Number(e.target.value)); 
                    if (setCurrentPage) setCurrentPage(1); 
                  }}
                >
                  {[5, 10, 20, 50, 100].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                {/* Jump -10 pages */}
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs"
                  onClick={() => setCurrentPage && setCurrentPage((p: number) => Math.max(1, p - 10))}
                  disabled={currentPage <= 10}
                >
                  &#171;
                </button>
                {/* Previous page */}
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs"
                  onClick={() => setCurrentPage && setCurrentPage((p: number) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {/* Current page */}
                <button
                  className="px-2 py-1 rounded text-xs bg-blue-600 text-white cursor-default"
                  disabled
                >
                  {currentPage}
                </button>
                {/* Next page (show only if not last page) */}
                {currentPage < 100 && (
                  <button
                    className="px-2 py-1 rounded text-xs bg-gray-200 text-gray-700 hover:bg-gray-300"
                    onClick={() => setCurrentPage && setCurrentPage(currentPage + 1)}
                  >
                    {currentPage + 1}
                  </button>
                )}
                {/* Next page button */}
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs"
                  onClick={() => setCurrentPage && setCurrentPage((p: number) => p + 1)}
                  disabled={displayCustomers.length < rowsPerPage}
                >
                  Next
                </button>
                {/* Jump +10 pages */}
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs"
                  onClick={() => setCurrentPage && setCurrentPage((p: number) => p + 10)}
                  disabled={displayCustomers.length < rowsPerPage}
                >
                  &#187;
                </button>
              </div>
              <div className="text-xs text-gray-600">
                {query.trim().length > 0 ? (
                  `Found ${displayCustomers.length} search results`
                ) : (
                  `Showing ${displayCustomers.length} of ${displayCustomers.length} (Page ${currentPage})`
                )}
              </div>
            </div>
          )}
          {/* Show search results info when searching */}
          {query.trim().length > 0 && (
            <div className="text-xs text-gray-600 mt-4 text-center">
              Found {displayCustomers.length} search results for "{query}"
            </div>
          )}
          {/* Show filter results info when filtering */}
          {isFiltering && (
            <div className="text-xs text-gray-600 mt-4 text-center">
              Filtering customers...
            </div>
          )}
        </>
      )}
      {showWalletDialog && selectedCustomerForWallet && (
        <CustomerWalletDialog
          customer={selectedCustomerForWallet}
          onClose={() => { setShowWalletDialog(false); setSelectedCustomerForWallet(null); }}
        />
      )}
    </>
  );
}